/* ==========================================================================
   OrangeWear — product.js
   Product page components (requires global.js / window.theme):
   <product-info>, <product-form>, <media-gallery>, <sticky-add-to-cart>,
   <delivery-estimate>, <share-button>, <product-recommendations>.
   ========================================================================== */
(() => {
  const theme = (window.theme = window.theme || {});

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const escapeHTML = (value) =>
    String(value == null ? '' : value).replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );

  const formatMoney = (cents, format) => {
    if (typeof theme.formatMoney === 'function') return theme.formatMoney(cents, format);
    return (Number(cents || 0) / 100).toFixed(2);
  };

  const debounce = (fn, wait) => {
    if (typeof theme.debounce === 'function') return theme.debounce(fn, wait);
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const emit = (name, detail) => {
    if (typeof theme.emit === 'function') {
      theme.emit(name, detail);
    } else {
      document.dispatchEvent(new CustomEvent(`theme:${name}`, { detail }));
    }
  };

  const isDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  /* ------------------------------------------------------------------------
     <product-info>: variant selection for the main product section.
     ------------------------------------------------------------------------ */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const dataElement = this.querySelector(':scope > script[data-product-json]');
      if (!dataElement) return;
      try {
        this.data = JSON.parse(dataElement.textContent);
      } catch (error) {
        return;
      }

      this.variants = Array.isArray(this.data.variants) ? this.data.variants : [];
      this.strings = this.data.strings || {};
      this.sectionId = this.dataset.section;
      this.picker = this.querySelector('[data-variant-picker]');
      if (!this.picker || !this.variants.length) return;

      const selected = this.getSelectedOptions();
      this.currentVariant = this.findVariant(selected);
      this.lastFeaturedMedia = this.currentVariant ? this.currentVariant.featured_media : null;
      this.updateOptionStates(selected);

      this.picker.addEventListener('change', this.onOptionChange);
    }

    disconnectedCallback() {
      if (this.picker) this.picker.removeEventListener('change', this.onOptionChange);
    }

    get optionGroups() {
      return Array.from(this.picker.querySelectorAll('[data-option-index]'));
    }

    getSelectedOptions() {
      const selected = [];
      this.optionGroups.forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        const select = group.querySelector('select[data-option-input]');
        if (select) {
          selected[index] = select.value;
          return;
        }
        const checked = group.querySelector('input[data-option-input]:checked');
        selected[index] = checked ? checked.value : null;
      });
      return selected;
    }

    findVariant(selected) {
      return (
        this.variants.find((variant) =>
          variant.options.every((value, index) => selected[index] == null || value === selected[index])
        ) || null
      );
    }

    onOptionChange = (event) => {
      if (!event.target.matches('[data-option-input]')) return;
      const selected = this.getSelectedOptions();
      const variant = this.findVariant(selected);
      this.currentVariant = variant;

      this.updateOptionStates(selected);
      this.updateSelectedLabels(selected);
      this.updateIdInputs(variant);
      this.updateButtons(variant);
      this.updatePrice(variant);
      this.updateVariantTitle(variant, selected);
      this.updateURL(variant);
      this.updateMedia(variant, selected);
      this.clearErrors();

      emit('variant:changed', { variant, sectionId: this.sectionId });
    };

    /* Availability follows Shopify's option value rules: a value is available when a purchasable
       variant exists for the values selected in the preceding options. */
    updateOptionStates(selected) {
      this.optionGroups.forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        const preceding = selected.slice(0, index);
        const candidates = this.variants.filter((variant) =>
          preceding.every((value, position) => variant.options[position] === value)
        );

        group.querySelectorAll('input[data-option-input], select[data-option-input] option').forEach((input) => {
          const matches = candidates.filter((variant) => variant.options[index] === input.value);
          let state = 'available';
          if (!matches.length) {
            state = 'unavailable';
          } else if (!matches.some((variant) => variant.available)) {
            state = 'sold-out';
          }
          input.dataset.state = state;

          let status = '';
          if (state === 'sold-out') status = this.strings.optionSoldOut || '';
          if (state === 'unavailable') status = this.strings.optionUnavailable || '';

          if (input.tagName === 'OPTION') {
            input.textContent = status ? `${input.value} – ${status}` : input.value;
            return;
          }

          const label =
            input.nextElementSibling && input.nextElementSibling.htmlFor === input.id
              ? input.nextElementSibling
              : group.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          const statusElement = label ? label.querySelector('[data-option-status]') : null;
          if (statusElement) statusElement.textContent = status ? `, ${status}` : '';
        });
      });
    }

    updateSelectedLabels(selected) {
      this.optionGroups.forEach((group) => {
        const label = group.querySelector('[data-selected-value]');
        if (label) label.textContent = selected[Number(group.dataset.optionIndex)] || '';
      });
    }

    updateIdInputs(variant) {
      this.querySelectorAll('input[data-variant-id-input]').forEach((input) => {
        if (variant) {
          input.value = variant.id;
          input.disabled = false;
        } else {
          input.disabled = true;
        }
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateButtons(variant) {
      let label = this.strings.addToCart || theme.strings.addToCart;
      let disabled = false;
      if (!variant) {
        label = this.strings.unavailable || theme.strings.unavailable;
        disabled = true;
      } else if (!variant.available) {
        label = this.strings.soldOut || theme.strings.soldOut;
        disabled = true;
      }

      this.querySelectorAll('[data-add-button]').forEach((button) => {
        button.disabled = disabled;
        const text = button.querySelector('[data-add-button-text]');
        if (text) text.textContent = label;
      });

      this.querySelectorAll('[data-payment-button]').forEach((element) => {
        element.hidden = disabled;
      });
    }

    updatePrice(variant) {
      this.querySelectorAll('[data-product-price]').forEach((container) => {
        if (!variant) {
          container.classList.add('is-unavailable');
          return;
        }
        container.classList.remove('is-unavailable');
        container.innerHTML = this.buildPriceHTML(variant, container.dataset.priceSize);
      });
    }

    /* Mirrors snippets/price.liquid (variant mode). */
    buildPriceHTML(variant, size) {
      const strings = this.strings;
      const onSale = variant.compare_at_price != null && variant.compare_at_price > variant.price;
      const classes = ['price'];
      if (onSale) classes.push('price--on-sale');
      if (size === 'large') classes.push('price--large');

      let html = `<div class="${classes.join(' ')}" data-price>`;
      if (onSale) html += `<span class="visually-hidden">${escapeHTML(strings.salePrice)}</span>`;
      html += `<span class="price__current">${formatMoney(variant.price)}</span>`;
      if (onSale) {
        html += `<span class="visually-hidden">${escapeHTML(strings.regularPrice)}</span>`;
        html += `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`;
      }
      const measurement = variant.unit_price_measurement;
      if (variant.unit_price != null && measurement) {
        const referenceValue =
          measurement.reference_value && Number(measurement.reference_value) !== 1 ? measurement.reference_value : '';
        html += '<span class="price__unit">';
        html += `<span class="visually-hidden">${escapeHTML(strings.unitPrice)}</span>`;
        html += formatMoney(variant.unit_price, theme.settings.moneyFormat);
        html += '<span aria-hidden="true">/</span>';
        html += `<span class="visually-hidden">${escapeHTML(strings.unitPriceSeparator)}</span>`;
        html += `${escapeHTML(referenceValue)}${escapeHTML(measurement.reference_unit)}`;
        html += '</span>';
      }
      html += '</div>';
      return html;
    }

    updateVariantTitle(variant, selected) {
      const title = variant ? variant.title : selected.filter(Boolean).join(' / ');
      this.querySelectorAll('[data-variant-title]').forEach((element) => {
        element.textContent = title;
      });
    }

    updateURL(variant) {
      if (!variant || this.dataset.updateUrl !== 'true') return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        /* History API unavailable: keep the current URL. */
      }
    }

    updateMedia(variant, selected) {
      const gallery = this.querySelector('media-gallery');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

      const colorIndex = Number(gallery.dataset.colorIndex);
      const hasColor = colorIndex >= 0 && selected[colorIndex] != null;
      if (hasColor && gallery.dataset.hideVariantMedia === 'true') gallery.filterByColor(selected[colorIndex]);

      let featuredMedia = variant ? variant.featured_media : null;
      if (!featuredMedia && hasColor) {
        /* No variant for this combination: still show the selected colour. */
        const sameColor = this.variants.find(
          (candidate) => candidate.options[colorIndex] === selected[colorIndex] && candidate.featured_media
        );
        featuredMedia = sameColor ? sameColor.featured_media : null;
      }
      if (featuredMedia && featuredMedia !== this.lastFeaturedMedia) {
        gallery.setActiveMedia(featuredMedia);
      }
      if (featuredMedia) this.lastFeaturedMedia = featuredMedia;
    }

    clearErrors() {
      this.querySelectorAll('[data-form-error]').forEach((element) => {
        element.textContent = '';
      });
    }
  }

  define('product-info', ProductInfo);

  /* ------------------------------------------------------------------------
     <product-form>: adds the product form to the cart with theme.cart.add().
     ------------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    }

    get scope() {
      return this.closest('product-info') || this;
    }

    onSubmit = async (event) => {
      /* Without the cart API, fall back to the normal form submission. */
      if (!theme.cart || typeof theme.cart.add !== 'function') return;
      event.preventDefault();
      if (this.busy) return;

      const mainButton = this.querySelector('[data-add-button]');
      const submitter =
        event.submitter && event.submitter.matches('[data-add-button]') ? event.submitter : mainButton;
      const idInput = this.form.querySelector('input[data-variant-id-input]');
      if ((submitter && submitter.disabled) || (idInput && (idInput.disabled || !idInput.value))) return;

      this.busy = true;
      this.clearErrors();
      if (submitter) {
        submitter.classList.add('is-loading');
        submitter.setAttribute('aria-disabled', 'true');
      }

      try {
        await theme.cart.add(new FormData(this.form));
      } catch (error) {
        this.showError((error && error.message) || theme.strings.cartError, submitter);
      } finally {
        this.busy = false;
        if (submitter) {
          submitter.classList.remove('is-loading');
          submitter.removeAttribute('aria-disabled');
        }
      }
    };

    clearErrors() {
      this.scope.querySelectorAll('[data-form-error]').forEach((element) => {
        element.textContent = '';
      });
    }

    showError(message, submitter) {
      const sticky = submitter ? submitter.closest('sticky-add-to-cart') : null;
      const target =
        (sticky && sticky.querySelector('[data-form-error]')) || this.querySelector('[data-form-error]');
      if (target) target.textContent = message;
    }
  }

  define('product-form', ProductForm);

  /* ------------------------------------------------------------------------
     <media-gallery>: slider, thumbnails, counter, colour filter and zoom dialog.
     ------------------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.thumbList = this.querySelector('.product-gallery__thumbs');
      this.dots = this.querySelector('[data-gallery-dots]');
      this.counter = this.querySelector('[data-gallery-counter]');
      this.zoomDialog = this.querySelector('[data-zoom-dialog]');
      this.desktopQuery = window.matchMedia('(min-width: 990px)');

      this.onScroll = debounce(() => this.syncFromScroll(), 80);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onUserScroll, { passive: true });
      this.track.addEventListener('wheel', this.onUserScroll, { passive: true });
      this.addEventListener('click', this.onClick);
      this.desktopQuery.addEventListener('change', this.onBreakpointChange);

      const active = this.activeItem;
      if (active && active !== this.visibleItems[0]) {
        requestAnimationFrame(() => this.scrollToItem(active, 'auto'));
      }
      this.loadModels();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onUserScroll);
      this.track.removeEventListener('wheel', this.onUserScroll);
      this.removeEventListener('click', this.onClick);
      this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
    }

    get items() {
      return Array.from(this.track.children);
    }

    get visibleItems() {
      return this.items.filter((item) => !item.hidden);
    }

    get activeItem() {
      return this.visibleItems.find((item) => item.classList.contains('is-active')) || this.visibleItems[0] || null;
    }

    get isGrid() {
      return this.classList.contains('product-gallery--grid') && this.desktopQuery.matches;
    }

    onClick = (event) => {
      const thumb = event.target.closest('[data-thumb]');
      if (thumb && this.contains(thumb)) {
        this.setActiveMedia(thumb.dataset.thumb);
        return;
      }
      const zoomButton = event.target.closest('[data-zoom-open]');
      if (zoomButton && this.contains(zoomButton)) {
        this.openZoom(zoomButton.dataset.zoomOpen, zoomButton);
      }
    };

    onUserScroll = () => {
      this.pendingScroll = null;
    };

    onBreakpointChange = () => {
      const active = this.activeItem;
      if (active && !this.isGrid) this.scrollToItem(active, 'auto');
    };

    /* Public: used by <product-info> when the variant changes. */
    setActiveMedia(mediaId, { behavior } = {}) {
      const item = this.items.find((element) => element.dataset.mediaId === String(mediaId));
      if (!item || item.hidden) return;
      this.markActive(item);
      if (!this.isGrid) this.scrollToItem(item, behavior);
    }

    /* Public: only show media without a colour or of the given colour. */
    filterByColor(color) {
      if (color == null) return;
      const matches = (element) => !element.dataset.mediaColor || element.dataset.mediaColor === String(color);
      const hasMatches = this.items.some((item) => item.dataset.mediaColor === String(color));
      const apply = (element) => {
        element.hidden = hasMatches ? !matches(element) : false;
      };

      this.items.forEach(apply);
      this.querySelectorAll('.product-gallery__thumb-item, .product-zoom__item').forEach(apply);

      const visible = this.visibleItems;
      this.items.forEach((item) => item.classList.toggle('product-gallery__item--first', item === visible[0]));
      this.relabel(visible);

      const active = this.activeItem;
      if (active) {
        this.markActive(active);
        if (!this.isGrid) this.scrollToItem(active, 'auto');
      }
    }

    scrollToItem(item, behavior) {
      const first = this.visibleItems[0];
      if (!first || !item) return;
      const left = item.offsetLeft - first.offsetLeft;
      const resolvedBehavior = behavior || (prefersReducedMotion() ? 'auto' : 'smooth');
      /* While a smooth scroll is on its way, scroll events must not mark the slides it passes as active. */
      this.pendingScroll =
        resolvedBehavior === 'smooth' && Math.abs(this.track.scrollLeft - left) > 2
          ? { left, expires: Date.now() + 1500 }
          : null;
      this.track.scrollTo({ left, behavior: resolvedBehavior });
    }

    syncFromScroll() {
      if (this.isGrid) return;
      if (this.pendingScroll) {
        const reached = Math.abs(this.track.scrollLeft - this.pendingScroll.left) <= 2;
        if (!reached && Date.now() < this.pendingScroll.expires) return;
        this.pendingScroll = null;
        if (reached) return;
      }
      const visible = this.visibleItems;
      if (!visible.length) return;
      const width = this.track.clientWidth || 1;
      const index = Math.max(0, Math.min(visible.length - 1, Math.round(this.track.scrollLeft / width)));
      const item = visible[index];
      if (item && !item.classList.contains('is-active')) this.markActive(item);
    }

    markActive(item) {
      const visible = this.visibleItems;
      const mediaId = item.dataset.mediaId;

      this.items.forEach((element) => element.classList.toggle('is-active', element === item));
      this.querySelectorAll('[data-thumb]').forEach((thumb) => {
        if (thumb.dataset.thumb === mediaId) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      this.updateCounter(visible.indexOf(item) + 1, visible.length);
      this.scrollThumbIntoView(mediaId);
      this.pauseInactiveMedia(item);
    }

    updateCounter(position, total) {
      this.classList.toggle('product-gallery--single-visible', total <= 1);
      if (this.counter) {
        const template = this.dataset.labelCounter || '[current] / [total]';
        this.counter.textContent = template.replace('[current]', position).replace('[total]', total);
      }
      if (this.dots) {
        if (this.dots.children.length !== total) {
          const dots = Array.from({ length: total }, () => {
            const dot = document.createElement('span');
            dot.className = 'product-gallery__dot';
            return dot;
          });
          this.dots.replaceChildren(...dots);
        }
        Array.from(this.dots.children).forEach((dot, index) => {
          dot.classList.toggle('is-active', index === position - 1);
        });
      }
    }

    scrollThumbIntoView(mediaId) {
      const list = this.thumbList;
      if (!list || list.scrollHeight <= list.clientHeight + 1) return;
      const thumb = list.querySelector(`[data-thumb="${CSS.escape(mediaId)}"]`);
      const listItem = thumb ? thumb.parentElement : null;
      if (!listItem || listItem.hidden) return;
      const top = listItem.offsetTop;
      const bottom = top + listItem.offsetHeight;
      if (top < list.scrollTop) {
        list.scrollTop = top;
      } else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = bottom - list.clientHeight;
      }
    }

    relabel(visible) {
      visible.forEach((item, position) => {
        const index = position + 1;
        const mediaId = item.dataset.mediaId;
        const thumb = this.querySelector(`[data-thumb="${CSS.escape(mediaId)}"]`);
        if (thumb) {
          const type = thumb.dataset.mediaType;
          let template = this.dataset.labelImage;
          if (type === 'video' || type === 'external_video') template = this.dataset.labelVideo;
          if (type === 'model') template = this.dataset.labelModel;
          if (template) thumb.setAttribute('aria-label', template.replace('[index]', index));
        }
        const zoomButton = item.querySelector('[data-zoom-open]');
        if (zoomButton && this.dataset.labelZoom) {
          zoomButton.setAttribute('aria-label', this.dataset.labelZoom.replace('[index]', index));
        }
      });
    }

    pauseInactiveMedia(activeItem) {
      this.items.forEach((item) => {
        if (item === activeItem) return;
        item.querySelectorAll('video').forEach((video) => {
          if (!video.paused) video.pause();
        });
        item.querySelectorAll('iframe').forEach((frame) => {
          try {
            frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            frame.contentWindow.postMessage('{"method":"pause"}', '*');
          } catch (error) {
            /* Ignore frames that cannot receive messages. */
          }
        });
        item.querySelectorAll('model-viewer').forEach((viewer) => {
          if (viewer.modelViewerUI && typeof viewer.modelViewerUI.pause === 'function') viewer.modelViewerUI.pause();
        });
      });
    }

    openZoom(mediaId, opener) {
      if (!this.zoomDialog || typeof theme.openDialog !== 'function') return;
      theme.openDialog(this.zoomDialog, opener);
      const list = this.zoomDialog.querySelector('.product-zoom__list');
      const target = this.zoomDialog.querySelector(`.product-zoom__item[data-media-id="${CSS.escape(mediaId)}"]`);
      if (list && target && !target.hidden) list.scrollTop = target.offsetTop;
    }

    loadModels() {
      if (!this.querySelector('model-viewer')) return;
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: (errors) => {
            if (errors || !window.Shopify.ModelViewerUI) return;
            this.querySelectorAll('model-viewer').forEach((viewer) => {
              if (!viewer.modelViewerUI) viewer.modelViewerUI = new window.Shopify.ModelViewerUI(viewer);
            });
          },
        },
      ]);
    }
  }

  define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------------------
     <sticky-add-to-cart>: shown while the main add to cart button is scrolled
     past and the footer is not in view.
     ------------------------------------------------------------------------ */
  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      const productInfo = this.closest('product-info');
      this.mainButton = productInfo ? productInfo.querySelector('[data-main-add-to-cart] [data-add-button]') : null;
      if (!this.mainButton || !('IntersectionObserver' in window)) return;

      this.isVisible = false;
      this.pastButton = false;
      this.visibleFooters = new Set();

      /* The root is extended far below the viewport, so the button only stops intersecting once it has
         scrolled above the top edge. Jumps (End key, anchors, restored scroll) still change the state. */
      this.buttonObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          this.pastButton = !entry.isIntersecting && entry.boundingClientRect.bottom < window.innerHeight;
          this.update();
        },
        { rootMargin: '0px 0px 100000px 0px' }
      );
      this.buttonObserver.observe(this.mainButton);

      let footers = Array.from(document.querySelectorAll('.shopify-section-group-footer-group'));
      if (!footers.length) footers = Array.from(document.querySelectorAll('footer'));
      if (footers.length) {
        this.footerObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.visibleFooters.add(entry.target);
            } else {
              this.visibleFooters.delete(entry.target);
            }
          });
          this.update();
        });
        footers.forEach((footer) => this.footerObserver.observe(footer));
      }
    }

    disconnectedCallback() {
      if (this.buttonObserver) this.buttonObserver.disconnect();
      if (this.footerObserver) this.footerObserver.disconnect();
    }

    update() {
      const visible = this.pastButton && this.visibleFooters.size === 0;
      if (visible === this.isVisible) return;
      this.isVisible = visible;

      if (!visible && this.contains(document.activeElement)) {
        this.mainButton.focus({ preventScroll: true });
      }
      this.classList.toggle('is-visible', visible);
      this.toggleAttribute('inert', !visible);
      this.setAttribute('aria-hidden', String(!visible));
    }
  }

  define('sticky-add-to-cart', StickyAddToCart);

  /* ------------------------------------------------------------------------
     <delivery-estimate>: business-day delivery window from theme settings.
     ------------------------------------------------------------------------ */
  const toDateKey = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const addBusinessDays = (start, count, holidays) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    let added = 0;
    let guard = 0;
    while (added < count && guard < 730) {
      date.setDate(date.getDate() + 1);
      guard += 1;
      const day = date.getDay();
      if (day !== 0 && day !== 6 && !holidays.has(toDateKey(date))) added += 1;
    }
    return date;
  };

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const datesLine = this.querySelector('[data-delivery-dates]');
      const range = this.querySelector('[data-range]');
      const delivery = theme.settings && theme.settings.delivery;
      if (!datesLine || !range || !delivery) return;

      const toCount = (value) => {
        const number = Number.parseInt(value, 10);
        return Number.isFinite(number) && number >= 0 ? number : null;
      };
      const processingMin = toCount(delivery.processingMin);
      const processingMax = toCount(delivery.processingMax);
      const transitMin = toCount(delivery.transitMin);
      const transitMax = toCount(delivery.transitMax);
      const values = [processingMin, processingMax, transitMin, transitMax];

      if (values.includes(null) || processingMax < processingMin || transitMax < transitMin) {
        datesLine.hidden = true;
        return;
      }

      const holidays = new Set(
        (Array.isArray(delivery.holidays) ? delivery.holidays : [])
          .map((value) => String(value).trim())
          .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))
      );

      const today = new Date();
      const earliest = addBusinessDays(today, processingMin + transitMin, holidays);
      const latest = addBusinessDays(today, processingMax + transitMax, holidays);

      let formatter;
      try {
        formatter = new Intl.DateTimeFormat('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
      } catch (error) {
        datesLine.hidden = true;
        return;
      }

      const start = formatter.format(earliest);
      const end = formatter.format(latest);
      const template = this.dataset.rangeTemplate || '[start] – [end]';
      range.textContent = start === end ? start : template.replace('[start]', start).replace('[end]', end);
      datesLine.hidden = false;
    }
  }

  define('delivery-estimate', DeliveryEstimate);

  /* ------------------------------------------------------------------------
     <share-button>: native share sheet, clipboard, or a selectable link.
     ------------------------------------------------------------------------ */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-share-button]');
      this.label = this.querySelector('[data-share-label]');
      this.status = this.querySelector('[data-share-status]');
      this.fallback = this.querySelector('[data-share-fallback]');
      if (!this.button) return;

      this.canShare = typeof navigator.share === 'function';
      if (this.label) {
        this.label.textContent = (this.canShare ? this.dataset.labelShare : this.dataset.labelCopy) || this.label.textContent;
      }
      this.button.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
      clearTimeout(this.statusTimer);
    }

    get shareUrl() {
      const url = new URL(this.dataset.url || window.location.href, window.location.origin);
      const variant = new URL(window.location.href).searchParams.get('variant');
      if (variant) url.searchParams.set('variant', variant);
      return url.toString();
    }

    onClick = async () => {
      const url = this.shareUrl;
      if (this.canShare) {
        try {
          await navigator.share({ title: this.dataset.title || document.title, url });
          return;
        } catch (error) {
          if (error && error.name === 'AbortError') return;
        }
      }

      try {
        await navigator.clipboard.writeText(url);
        this.showStatus(this.dataset.labelCopied || '');
      } catch (error) {
        this.showFallback(url);
      }
    };

    showStatus(message) {
      if (!this.status) return;
      clearTimeout(this.statusTimer);
      this.status.textContent = message;
      this.statusTimer = setTimeout(() => {
        this.status.textContent = '';
      }, 4000);
    }

    showFallback(url) {
      if (!this.fallback) return;
      const input = this.fallback.querySelector('input');
      this.fallback.hidden = false;
      if (input) {
        input.value = url;
        input.focus();
        input.select();
      }
    }
  }

  define('share-button', ShareButton);

  /* ------------------------------------------------------------------------
     <product-recommendations>: loads related products near the viewport.
     ------------------------------------------------------------------------ */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded || !this.dataset.url) return;
      if (this.querySelector('.product-card')) {
        this.loaded = true;
        return;
      }

      if (!('IntersectionObserver' in window) || isDesignMode()) {
        this.load();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;
      try {
        const response = await fetch(this.dataset.url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(response.statusText);
        const html = await response.text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
        if (fresh && fresh.querySelector('.product-card')) {
          this.innerHTML = fresh.innerHTML;
          this.hidden = false;
        } else {
          this.hide();
        }
      } catch (error) {
        this.hide();
      }
    }

    hide() {
      this.replaceChildren();
      this.hidden = true;
      const section = this.closest('.shopify-section');
      if (section && !isDesignMode()) section.hidden = true;
    }
  }

  define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------------
     Theme editor: open a collapsible tab when its block is selected.
     ------------------------------------------------------------------------ */
  if (!window.__orangewearProductEditorHooks) {
    window.__orangewearProductEditorHooks = true;
    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      if (!(block instanceof HTMLElement) || !block.closest('product-info')) return;
      const details = block.matches('details') ? block : block.querySelector('details');
      if (details) details.open = true;
    });
  }
})();
